<script setup lang="ts">
import { isCurrencyCode } from '@taler/domain'
import { SearchableSelect } from '@taler/ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDisplayCurrency } from '@/app/display-currency'
import { useCurrencies } from '@/features/currencies'

const { t } = useI18n()
const { data: currencies } = useCurrencies()
const { displayCurrency, setDisplayCurrency } = useDisplayCurrency()

const options = computed(() =>
  currencies.value.flatMap((currency) =>
    isCurrencyCode(currency.code)
      ? [{ value: currency.code, label: `${currency.code} - ${currency.name}` }]
      : [],
  ),
)

const currencyName = computed(
  () => currencies.value.find((currency) => currency.code === displayCurrency.value)?.name ?? '',
)

function choose(value: string | readonly string[]): void {
  if (typeof value === 'string' && isCurrencyCode(value)) setDisplayCurrency(value)
}
</script>

<template>
  <SearchableSelect
    :model-value="displayCurrency"
    :options="options"
    :trigger-label="displayCurrency"
    :search-label="t('shell.searchCurrencies')"
    :empty-text="t('shell.noCurrenciesFound')"
    :aria-label="`${t('shell.displayCurrency')}: ${displayCurrency}`"
    fill
    @update:model-value="choose"
  >
    <template #trigger>
      <span class="font-semibold text-[color:var(--color-text)]">{{ displayCurrency }}</span>
      <span
        class="hidden min-w-0 flex-1 truncate text-left text-[color:var(--color-text-muted)] wide:block"
        >{{ currencyName }}</span
      >
    </template>
    <template #indicator>
      <svg
        width="14"
        height="14"
        viewBox="0 0 16 16"
        aria-hidden="true"
        class="hidden shrink-0 text-[color:var(--color-text-muted)] wide:block"
      >
        <path
          d="M5 6 8 3l3 3M5 10l3 3 3-3"
          fill="none"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </template>
  </SearchableSelect>
</template>
